<template>
  <highcharts :options="chartOptions" />
</template>

<script>
import Highcharts from 'highcharts';
import { Chart } from 'highcharts-vue';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import exportingInit from 'highcharts/modules/exporting';

Highcharts.setOptions({
  lang: {
    decimalPoint: ',',
  },
});

highchartsMore(Highcharts);
solidGauge(Highcharts);
exportingInit(Highcharts);

export default {
  name: 'GaugeChart',

  components: {
    highcharts: Chart,
  },

  props: {
    titulo: {
      type: String,
      required: false,
      default: '',
    },
    fonte: {
      type: String,
      required: true,
    },
    descricaoTitulo: {
      type: String,
      required: true,
    },
    descricao: {
      type: String,
      required: true,
    },
    minimo: {
      type: Number,
      required: false,
      default: 0,
    },
    maximo: {
      type: Number,
      required: false,
      default: 10,
    },
    escalas: {
      type: Array,
      required: false,
      default: () => [0, 4, 8, 10],
    },
    formato: {
      type: String,
      required: false,
      default: '',
    },
    dados: {
      type: Array,
      required: true,
    },
  },

  computed: {
    chartOptions() {
      return {
        chart: {
          type: 'solidgauge',
          style: {
            fontFamily: 'Roboto;Arial;sans-serif',
          },
          events: {
            render() {
              const chart = this;

              if (chart.fullscreen.isOpen && chart.fullscreenStatus === 'fullscreen: off') {
                chart.fullscreenStatus = 'fullscreen: on';

                chart.update({
                  chart: {
                    marginBottom: window.screen.height * 0.2,
                    height: window.screen.height,
                  },
                  pane: {
                    background: {
                      outerRadius: '98%',
                    },
                  },
                  yAxis: {
                    title: {
                      style: {
                        fontSize: '20px',
                      },
                    },
                    labels: {
                      style: {
                        fontSize: '20px',
                      },
                    },
                  },
                  tooltip: {
                    style: {
                      fontSize: '20px',
                    },
                  },
                });
              }

              if (!chart.fullscreen.isOpen && chart.fullscreenStatus === 'fullscreen: on') {
                chart.fullscreenStatus = 'fullscreen: off';

                chart.update({
                  chart: {
                    marginBottom: chart.initialPlotBottom,
                    height: 400,
                  },
                  pane: {
                    background: {
                      outerRadius: '95%',
                    },
                  },
                  yAxis: {
                    title: {
                      style: {
                        fontSize: '12px',
                      },
                    },
                    labels: {
                      style: {
                        fontSize: '11px',
                      },
                    },
                  },
                  tooltip: {
                    style: {
                      fontSize: '12px',
                    },
                  },
                });
              }
            },
            load() {
              const chart = this;
              chart.initialPlotBottom = chart.plotBottom;
              chart.fullscreenStatus = 'fullscreen: off';
            },
          },
        },
        credits: {
          enabled: true,
        },
        title: {
          text: this.titulo === '' ? '' : this.titulo,
        },
        subtitle: {
          text: this.fonte,
        },
        pane: {
          center: ['50%', '85%'],
          size: '140%',
          startAngle: -90,
          endAngle: 90,
          background: {
            backgroundColor: Highcharts.defaultOptions.legend.backgroundColor || '#EEE',
            innerRadius: '60%',
            outerRadius: '95%',
            shape: 'arc',
          },
        },
        yAxis: {
          min: parseInt(this.minimo, 10),
          max: parseInt(this.maximo, 10),
          stops: [
            [0.1, '#348ECA'],
            [0.4, '#2D5269'],
            [0.8, '#00ABA4'],
          ],
          lineWidth: 0,
          minorTickInterval: null,
          tickAmount: 2,
          tickPixelInterval: 30,
          tickWidth: 2,
          tickPosition: 'outside',
          tickLength: 5,
          tickColor: '#cccccc',
          tickPositions: this.escalas,
          labels: {
            y: 16,
            distance: 25,
          },
          plotBands: [
            { from: 0, to: 4, color: '#348ECA' },
            { from: 4, to: 8, color: '#2D5269' },
            { from: 8, to: 10, color: '#00ABA4' },
          ],
        },
        plotOptions: {
          solidgauge: {
            dataLabels: {
              y: 5,
              borderWidth: 0,
              useHTML: true,
            },
          },
        },
        exporting: {
          buttons: {
            contextButton: {
              menuItems: [
                'viewFullscreen',
                'printChart',
                'separator',
                'downloadJPEG',
                'downloadPNG',
                'downloadPDF',
                'downloadSVG',
              ],
            },
          },
          menuItemDefinitions: {
            viewFullscreen: {
              textKey: 'viewFullscreen',
              onclick() {
                this.update({
                  tooltip: {
                    outside: this.fullscreen.isOpen,
                  },
                });
                this.fullscreen.toggle();
              },
            },
          },
        },
        lang: {
          contextButtonTitle: 'Opções do Gráfico',
          decimalPoint: ',',
          viewFullscreen: 'Ver em Tela Cheia',
          printChart: 'Imprimir Gráfico',
          downloadJPEG: 'Baixar como Imagem JPEG',
          downloadPNG: 'Baixar como Imagem PNG',
          downloadPDF: 'Baixar como Documento PDF',
          downloadSVG: 'Baixar como Vetor de Imagem SVG',
          exitFullscreen: 'Sair do modo Tela Cheia',
          loading: 'Carregando...',
          noData: 'Não há dados disponíveis',
        },
        series: [
          {
            name: 'gauge',
            data: this.dados,
            dataLabels: {
              formatter() {
                let color = '';
                if (this.point.y >= 9) {
                  color = 'color: #00ABA4';
                }
                return (
                  `<div style="text-align:center"><span style="font-size:42px;${color}">${
                    Highcharts.numberFormat(this.point.y, 2)}</span></div>`
                );
              },
              y: -20,
            },
            tooltip: {
              headerFormat: `<b>${this.descricaoTitulo}</b><br/><table>`,
              pointFormat: `<tr><td>${this.descricao}</td></tr>`,
              footerFormat: '</table>',
              hideDelay: 100,
              shared: true,
              useHTML: true,
              enabled: false,
            },
          },
        ],
      };
    },
  },
};
</script>
